import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Eye, Filter, SlidersHorizontal, X } from 'lucide-react';
import { type BoardView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { tagTextClassOf } from '../../../components/ui/task-type-tag';
import { cn } from '../../../lib/utils';
import {
  type BoardFilter,
  type DueBucket,
  NO_BOARD_FILTER,
  assigneesOf,
  isFilterActive,
  taskTypesOf,
} from '../filter';

const DUE_BUCKETS: readonly DueBucket[] = ['overdue', 'today', 'upcoming'];

/**
 * Hàng lọc/làm nổi của board — APM-BOARD-f.
 *
 * ## Chế độ đứng **trước** tiêu chí
 *
 * Đọc thành một câu từ trái sang: "làm nổi / lọc — việc của [ai] — loại
 * [gì]". Đặt chế độ ở cuối thì người dùng chọn xong tiêu chí mới phát hiện nó
 * đang giấu mất phần còn lại của board, và phải quay lại sửa.
 *
 * ## Danh sách lấy từ **thẻ đang có trên board**
 *
 * Không lấy từ danh mục của dự án: một dự án khai mười loại việc nhưng sprint
 * này chỉ dùng ba, và bảy nút không bao giờ khớp gì chỉ làm hàng nút dài ra.
 * Xem `assigneesOf` / `taskTypesOf`.
 */
/**
 * Một ô lọc: nhãn + ô chọn — APM-PSET-h.
 *
 * ## Vì sao hai ô một hàng ở màn dọc
 *
 * Bản trước để mỗi ô co theo nội dung, nên ở cột 356px chúng xuống **ba hàng
 * riêng** và nửa phải của mỗi hàng bỏ trống — thanh lọc cao 140px trước khi
 * thấy việc đầu tiên. Trên màn dọc, chiều cao là thứ đắt nhất.
 *
 * `basis-36 grow` cho chúng chia nhau chỗ: hai ô một hàng ở 356px, ba ô một
 * hàng khi rộng hơn. `@sm:max-w-48` chặn lại khi vùng nội dung đã rộng — không
 * có nó thì ở màn ngang ba ô chọn kéo dài hết bề ngang cho ba giá trị ngắn.
 */
const FILTER_LABEL =
  'flex min-w-0 grow basis-36 items-center gap-1.5 text-xs text-muted-foreground @sm:max-w-48';

/**
 * Ở điện thoại ba ô chọn **gập** sau nút "Tiêu chí" — người dùng yêu cầu
 * 2026-10-07. Kể cả xếp hai ô một hàng, thanh lọc vẫn chiếm hai hàng rưỡi ở
 * đầu tab, trong khi phần lớn lượt mở tab không đổi tiêu chí nào. Màn rộng
 * vẫn bày sẵn: ở đó chúng nằm gọn trên một hàng.
 *
 * Đo **cửa sổ** (`max-sm:`), cùng mốc 640px với `useNarrowScreen` — câu hỏi là
 * "đây có phải điện thoại không", không phải "cột này rộng bao nhiêu".
 */
const COLLAPSED_ON_PHONE = 'max-sm:hidden';

/** Ô chọn lấp đầy phần còn lại của nhãn, thay vì co theo giá trị đang chọn. */
const FILTER_SELECT =
  'h-7 min-w-0 flex-1 rounded-control border border-border-strong bg-background px-2 text-xs';

export function BoardFilterBar({
  board,
  value,
  onChange,
}: {
  readonly board: BoardView;
  readonly value: BoardFilter;
  readonly onChange: (next: BoardFilter) => void;
}): ReactNode {
  const { t } = useTranslation();
  const assignees = assigneesOf(board);
  const taskTypes = taskTypesOf(board);
  const active = isFilterActive(value);
  const [expanded, setExpanded] = useState(false);
  // Đếm tiêu chí đang đặt để nút gập nói ra được "đang lọc 2 thứ" — gập lại mà
  // không có con số thì người dùng thấy board thiếu việc mà không biết vì sao.
  const criteria = [value.assigneeId, value.taskTypeId, value.due].filter(
    (item) => item !== null,
  ).length;
  const criteriaClass = expanded ? undefined : COLLAPSED_ON_PHONE;

  // Trục "thời hạn" không cần dữ liệu gì để có ích, nên hàng nút này luôn trả
  // lời được ít nhất một câu hỏi — khác hai trục kia, chúng tự ẩn khi board
  // chưa có ai phụ trách hoặc chỉ có một loại việc.

  const selectedType = taskTypes.find((type) => type.id === value.taskTypeId) ?? null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-DEFAULT border border-border bg-muted/30 px-2 py-1.5 max-sm:px-1.5 max-sm:py-1">
      <GlassSegmented
        size="sm"
        ariaLabel={t('boardFilter.modeSwitch')}
        value={value.mode}
        options={(['highlight', 'filter'] as const).map((mode) => {
          const Icon = mode === 'highlight' ? Eye : Filter;
          return {
            value: mode,
            label: (
              <>
                <Icon aria-hidden className="size-3.5" />
                {t(`boardFilter.mode.${mode}`)}
              </>
            ),
          };
        })}
        onChange={(mode) => onChange({ ...value, mode })}
      />

      <Button
        size="sm"
        variant={criteria > 0 ? 'secondary' : 'ghost'}
        className="ml-auto h-7 gap-1 px-2 text-xs sm:hidden"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
      >
        <SlidersHorizontal aria-hidden className="size-3.5" />
        {t('boardFilter.criteria')}
        {criteria > 0 ? (
          <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold tabular-nums text-primary-foreground">
            {criteria}
          </span>
        ) : null}
        <ChevronDown
          aria-hidden
          className={cn('size-3.5 transition-transform', expanded && 'rotate-180')}
        />
      </Button>

      {assignees.length > 0 ? (
        <label className={cn(FILTER_LABEL, criteriaClass)}>
          {t('boardFilter.assignee')}
          <select
            className={cn(FILTER_SELECT, 'text-foreground')}
            value={value.assigneeId ?? ''}
            onChange={(event) => onChange({ ...value, assigneeId: event.target.value || null })}
          >
            <option value="">{t('boardFilter.anyAssignee')}</option>
            {assignees.map((assignee) => (
              <option key={assignee.id} value={assignee.id}>
                {assignee.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {taskTypes.length > 0 ? (
        <label className={cn(FILTER_LABEL, criteriaClass)}>
          {t('boardFilter.taskType')}
          {/*
            Giữ **màu** của loại việc ngay trong ô chọn: màu là thứ nhận ra nó
            trên thẻ, nên ô chọn mà toàn chữ đen thì người dùng phải đọc tên
            rồi tự nối với cái nhãn màu ngoài board.

            `<option>` chỉ nhận được **màu chữ** — nền, viền, bo góc đều bị
            trình duyệt bỏ qua, nên ở đây không dùng `TaskTypeTag` mà lấy đúng
            lớp màu chữ của cùng phép băm (`tagTextClassOf`). Loại việc được
            admin đặt màu riêng thì màu đó là CSS thật, gán thẳng bằng `style`.
          */}
          <select
            className={cn(
              FILTER_SELECT,
              'font-medium',
              selectedType && !selectedType.color
                ? tagTextClassOf(selectedType.id)
                : 'text-foreground',
            )}
            style={selectedType?.color ? { color: selectedType.color } : undefined}
            value={value.taskTypeId ?? ''}
            onChange={(event) => onChange({ ...value, taskTypeId: event.target.value || null })}
          >
            <option value="" className="text-foreground">
              {t('boardFilter.anyTaskType')}
            </option>
            {taskTypes.map((type) => (
              <option
                key={type.id}
                value={type.id}
                className={cn('font-medium', type.color ? undefined : tagTextClassOf(type.id))}
                style={type.color ? { color: type.color } : undefined}
              >
                {type.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {/* Thời hạn đứng **cuối**: hai trục trên trả lời "của ai, loại gì" — thứ
          gắn với bản thân việc; trục này trả lời "gấp tới đâu" — thứ đổi theo
          từng ngày. Xếp cuối để thứ ổn định đứng trước thứ trôi. */}
      <label className={cn(FILTER_LABEL, criteriaClass)}>
        {t('boardFilter.due')}
        <select
          className={cn(FILTER_SELECT, 'text-foreground')}
          value={value.due ?? ''}
          onChange={(event) =>
            onChange({ ...value, due: (event.target.value || null) as DueBucket | null })
          }
        >
          <option value="">{t('boardFilter.anyDue')}</option>
          {DUE_BUCKETS.map((bucket) => (
            <option key={bucket} value={bucket}>
              {t(`boardFilter.dueBucket.${bucket}`)}
            </option>
          ))}
        </select>
      </label>

      {active ? (
        <Button
          size="sm"
          variant="ghost"
          // Điện thoại: nút gập đã giữ `ml-auto`, nên "Bỏ chọn" đứng sát nó —
          // còn đang gập thì chỉ còn dấu ✕ (tên vẫn có cho trình đọc màn hình).
          className={cn('h-7 text-xs sm:ml-auto', !expanded && 'max-sm:w-7 max-sm:px-0')}
          title={t('boardFilter.clear')}
          onClick={() => onChange({ ...NO_BOARD_FILTER, mode: value.mode })}
        >
          <X aria-hidden />
          <span className={expanded ? undefined : 'max-sm:sr-only'}>{t('boardFilter.clear')}</span>
        </Button>
      ) : null}
    </div>
  );
}
